import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useProducts, useBanners, useWishlist, useCategories } from '../hooks/useProducts';
import ProductGrid from '../components/product/ProductGrid';
import { useAuthStore } from '../stores/authStore';

// Map category slugs to translation keys
const categorySlugToKey: Record<string, string> = {
  'daily-deal': 'dailyDeal',
  'mens-clothing': 'mensClothing',
  'womens-clothing': 'womensClothing',
  'mens-shoes': 'mensShoes',
  'womens-shoes': 'womensShoes',
  'kids-shoes': 'kidsShoes',
  'watches': 'watches',
  'aaa-watches': 'aaaWatches',
  'bags': 'bags',
  'wallets': 'wallets',
  'jewelry': 'jewelry',
  'sunglasses': 'sunglasses',
  'sports-products': 'sportsProducts',
  'football-products': 'footballProducts',
  'hats-gloves': 'hatsGloves',
  'cosmetics': 'cosmetics',
  'accessories': 'accessories',
  'amazon': 'amazon',
  'recommended-supplier': 'recommendedSupplier',
};

// Flash deal countdown timer component
function FlashDealTimer() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [timeLeft, setTimeLeft] = useState({
    hours: 5,
    minutes: 42,
    seconds: 18,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) {
          seconds = 59;
          minutes--;
        }
        if (minutes < 0) {
          minutes = 59;
          hours--;
        }
        if (hours < 0) {
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flash-deal-timer">
      <div className="flash-deal-timer-box">{String(timeLeft.hours).padStart(2, '0')}</div>
      <span className="flash-deal-timer-separator">:</span>
      <div className="flash-deal-timer-box">{String(timeLeft.minutes).padStart(2, '0')}</div>
      <span className="flash-deal-timer-separator">:</span>
      <div className="flash-deal-timer-box">{String(timeLeft.seconds).padStart(2, '0')}</div>
      <span className="ms-2 text-sm text-gray-500 font-medium">
        {isRTL ? 'נותר' : 'left'}
      </span>
    </div>
  );
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const isRTL = i18n.dir() === 'rtl';

  const { data: productsData, isLoading: productsLoading } = useProducts({ limit: 20 });
  useBanners();
  const { data: categories } = useCategories();
  const { data: wishlist } = useWishlist(isAuthenticated);

  const wishlistIds = wishlist?.map((item) => item.id) || [];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Categories Section */}
      <section className="bg-white py-6">
        <div className="container mx-auto px-4">
          {categories && categories.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#0F2A44]">
                  {isRTL ? 'קנה לפי קטגוריה' : 'Shop by Category'}
                </h2>
                <Link to="/products" className="text-[#0F2A44] hover:underline font-medium text-sm flex items-center gap-1">
                  {isRTL ? 'צפה בהכל' : 'View All'}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                  </svg>
                </Link>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-4">
                {/* Hot Products Link */}
                <Link
                  to="/products?hotProduct=true"
                  className="flex flex-col items-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full flex items-center justify-center text-2xl sm:text-3xl group-hover:scale-110 transition-transform mb-2 shadow-sm">
                    🔥
                  </div>
                  <h3 className="text-[11px] sm:text-xs font-medium text-gray-700 text-center group-hover:text-[#0F2A44] transition-colors line-clamp-2 leading-tight">
                    {isRTL ? 'המוצרים החמים' : 'Hot Products'}
                  </h3>
                </Link>
                {/* Daily Deal Link */}
                <Link
                  to="/products?dailyDeal=true"
                  className="flex flex-col items-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-full flex items-center justify-center text-2xl sm:text-3xl group-hover:scale-110 transition-transform mb-2 shadow-sm">
                    ⚡
                  </div>
                  <h3 className="text-[11px] sm:text-xs font-medium text-gray-700 text-center group-hover:text-[#0F2A44] transition-colors line-clamp-2 leading-tight">
                    {isRTL ? 'הדיל היומי' : 'Daily Deals'}
                  </h3>
                </Link>
                {categories.filter(cat => cat.slug !== 'hot-products' && cat.slug !== 'daily-deal').slice(0, 14).map((category) => {
                  const translationKey = categorySlugToKey[category.slug];
                  const categoryName = translationKey
                    ? t(`categories.${translationKey}`, { defaultValue: category.name })
                    : category.name;
                  return (
                    <Link
                      key={category.id}
                      to={`/category/${category.slug}`}
                      className="flex flex-col items-center p-2 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center text-2xl sm:text-3xl group-hover:scale-110 transition-transform mb-2 shadow-sm">
                        {category.icon}
                      </div>
                      <h3 className="text-[11px] sm:text-xs font-medium text-gray-700 text-center group-hover:text-[#0F2A44] transition-colors line-clamp-2 leading-tight">
                        {categoryName}
                      </h3>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Flash Deals Section */}
      <section className="bg-white mt-3 py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚡</span>
              <div>
                <h2 className="text-xl font-bold text-[#0F2A44]">
                  {t('home.flashDeals')}
                </h2>
                <p className="text-gray-500 text-sm">
                  {t('home.flashDealsSubtitle')}
                </p>
              </div>
            </div>
            <FlashDealTimer />
          </div>

          <ProductGrid
            products={productsData?.products?.slice(0, 6) || []}
            wishlistIds={isAuthenticated ? wishlistIds : []}
            isLoading={productsLoading}
            emptyMessage={t('home.noProducts')}
          />
        </div>
      </section>

      {/* Featured Products */}
      <section className="bg-white mt-3 py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#0F2A44] flex items-center gap-2">
              🔥 {t('home.recentProducts')}
            </h2>
            <Link to="/products" className="text-[#0F2A44] hover:underline font-medium text-sm flex items-center gap-1">
              {isRTL ? 'צפה בהכל' : 'View All'}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </Link>
          </div>

          <ProductGrid
            products={productsData?.products || []}
            wishlistIds={isAuthenticated ? wishlistIds : []}
            isLoading={productsLoading}
            emptyMessage={t('home.noProducts')}
          />

          {productsData?.pagination && productsData.pagination.totalPages > 1 && (
            <div className="text-center mt-8">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-[#0F2A44] text-white font-semibold px-8 py-3 rounded-full hover:bg-[#1a3a5c] transition-colors"
              >
                {isRTL ? 'צפה בעוד מוצרים' : 'View More Products'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M7 16l-4-4m0 0l4-4m-4 4h18" : "M17 8l4 4m0 0l-4 4m4-4H3"} />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Join Community Banner */}
      <section className="bg-white mt-3 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-[#0F2A44] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isRTL ? 'הצטרפו לקהילה שלנו!' : 'Join Our Community!'}
            </h2>
            <p className="text-gray-300 mb-6 max-w-xl mx-auto">
              {isRTL
                ? 'קבלו התראות על מבצעים, הנחות בלעדיות וטיפים לקניות חכמות'
                : 'Get notifications about deals, exclusive discounts and smart shopping tips'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://t.me/+972559664818"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-sky-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-sky-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                Telegram
              </a>
              <a
                href="https://wa.me/972559664818"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-green-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white mt-3 py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-[#0F2A44]/10 rounded-full flex items-center justify-center text-[#0F2A44]">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{t('trust.quality')}</h3>
              <p className="text-xs text-gray-500 mt-1">{t('trust.qualityDesc')}</p>
            </div>

            <div className="text-center p-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-[#0F2A44]/10 rounded-full flex items-center justify-center text-[#0F2A44]">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{t('trust.prices')}</h3>
              <p className="text-xs text-gray-500 mt-1">{t('trust.pricesDesc')}</p>
            </div>

            <div className="text-center p-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-[#0F2A44]/10 rounded-full flex items-center justify-center text-[#0F2A44]">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{t('trust.shipping')}</h3>
              <p className="text-xs text-gray-500 mt-1">{t('trust.shippingDesc')}</p>
            </div>

            <div className="text-center p-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-[#0F2A44]/10 rounded-full flex items-center justify-center text-[#0F2A44]">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{t('trust.support')}</h3>
              <p className="text-xs text-gray-500 mt-1">{t('trust.supportDesc')}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
